import { GetUploadScreenshotUrlRequest } from '@eigtech/meetings-types'
import { OmitAuthHeaders } from '@eigtech/ui-shared-api'
import {
  ACCEPTED_PHOTO_TYPES,
  MAX_FILE_SIZE,
  maximumUploadsCount,
} from '@eigtech/ui-shared-documents-web'
import { GalleryUploadForm, makeFileWithPreviewSchema } from '@eigtech/ui-shared-files'
import { createForm } from '@eigtech/ui-shared-forms/shadcn'
import { z } from 'zod'
import { useUploadMultipleScreenshots } from '../../api/uploadScreenshot'

export type UploadMeetingScreenshotsProps = OmitAuthHeaders<GetUploadScreenshotUrlRequest>

export function UploadMeetingScreenshots({
  cohort,
  entityId,
  meetingId,
  startTime: __startTime,
}: UploadMeetingScreenshotsProps) {
  const form = useForm({ defaultValues: { files: [] } })

  const {
    mutation: { mutateAsync: uploadDocuments, isError: isUploadError, error: uploadError, reset },
    numberOfFilesUploaded,
    numberOfFiles,
    isPendingUploadUrl,
    isUploadUrlError,
  } = useUploadMultipleScreenshots({ cohort, entityId, meetingId })

  async function onSubmit({ files }: FileUploadForm) {
    reset()
    await uploadDocuments(
      files.map(({ file, ...meta }) => {
        const updatedMeta = {
          ...meta,
          ...(meta.title && { title: encodeURIComponent(meta.title) }),
          description: encodeURIComponent(meta.description),
        }

        return {
          file,

          'Content-Type': file.type,
          meta: updatedMeta,
        }
      })
    )
  }

  return (
    <GalleryUploadForm<typeof FileUploadSchema>
      ArrayField={ArrayField}
      GalleryItemFields={ImageFields}
      accept={ACCEPTED_PHOTO_TYPES.join(',')}
      acceptedTypesLabel="Images"
      form={form}
      formId={'uploadMeetingScreenshotsForm'}
      isPendingUploadUrl={isPendingUploadUrl}
      isUploadError={isUploadError}
      isUploadUrlError={isUploadUrlError}
      maxFiles={maximumUploadsCount}
      maxSize={MAX_FILE_SIZE}
      numberOfFiles={numberOfFiles}
      numberOfFilesUploaded={numberOfFilesUploaded}
      uploadError={uploadError}
      onSubmit={onSubmit}
    />
  )
}

function ArrayField({ children }: { children: React.ReactNode }) {
  return (
    <BaseArrayField emptyMessage="" label="" name="files">
      {children}
    </BaseArrayField>
  )
}

function ImageFields() {
  const { name } = useArrayFieldRowContext()

  return (
    <>
      <InputField label="File Title" name={`${name}.title`} />
      <TextareaField label="File Description" name={`${name}.description`} rows={5} />
    </>
  )
}

const invalidTypeError = 'Only image file types are accepted.'
const fileTooBigError = 'File size must be less than 20MB.'

const BaseFileSchema = z.instanceof(File).superRefine((file, ctx) => {
  if (ACCEPTED_PHOTO_TYPES.includes(file.type)) {
    //Approved file type
    if (file.size > MAX_FILE_SIZE) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        fatal: true,
        message: fileTooBigError,
      })
    }
  } else {
    //Non-video, valid file type
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      fatal: true,
      message: invalidTypeError,
    })
  }
})

const FileSchema = z.object({
  file: makeFileWithPreviewSchema(BaseFileSchema),
  title: z.string().max(50, 'String cannot contain more than 50 characters'),
  description: z.string().max(500, 'String cannot contain more than 500 characters'),
})

const FileUploadSchema = z.object({
  files: z
    .array(FileSchema)
    .refine((files) => files?.length >= 1, 'Files are required.')
    .refine(
      (files) => files?.length <= maximumUploadsCount,
      `Can only upload ${maximumUploadsCount} or fewer files at a time.`
    ),
})

type FileUploadForm = z.infer<typeof FileUploadSchema>

const {
  useForm,
  ArrayField: BaseArrayField,
  InputField,
  TextareaField,
  useArrayFieldRowContext,
} = createForm(FileUploadSchema, 'fileUploadForm')
